// src/screens/VehicleCatalogScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { vehiclesAPI, usersAPI } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { Search, ChevronRight, SlidersHorizontal, X } from 'lucide-react';

// New components
import FiltersSidebar from '../components/vehicles/FiltersSidebar';
import ActiveFiltersBar from '../components/vehicles/ActiveFiltersBar';
import VehicleResultCard from '../components/vehicles/VehicleResultCard';
import SortDropdown from '../components/vehicles/SortDropdown';

const VehicleCatalogScreen = () => {
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Favorites (ids only for quick lookup)
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  // Mobile filter drawer state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Initialize filters from URL params or defaults
  const getInitialFilters = () => ({
    make: searchParams.get('make') || '',
    model: searchParams.get('model') || '',
    type: searchParams.get('type') || '',
    location: searchParams.get('location') || '',
    radius: searchParams.get('radius') || '50',
    maxMileage: searchParams.get('maxMileage') || '',
    availability: searchParams.get('availability') || 'all',
    verifiedOnly: searchParams.get('verifiedOnly') === 'true',
    includeShippable: searchParams.get('includeShippable') === 'true',
  });

  const [filters, setFilters] = useState(getInitialFilters());
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'popular');

  // Update URL when filters change
  const updateURL = (newFilters, newSort) => {
    const params = {};

    // Only include non-empty filters in URL
    if (newFilters.make) params.make = newFilters.make;
    if (newFilters.model) params.model = newFilters.model;
    if (newFilters.type) params.type = newFilters.type;
    if (newFilters.location) params.location = newFilters.location;
    if (newFilters.radius !== '50') params.radius = newFilters.radius;
    if (newFilters.maxMileage) params.maxMileage = newFilters.maxMileage;
    if (newFilters.availability !== 'all') params.availability = newFilters.availability;
    if (newFilters.verifiedOnly) params.verifiedOnly = 'true';
    if (newFilters.includeShippable) params.includeShippable = 'true';
    if (newSort !== 'popular') params.sortBy = newSort;

    setSearchParams(params);
  };

  // Build API query params from filters
  const buildAPIParams = () => {
    const params = {};

    if (filters.make) params.make = filters.make;
    if (filters.model) params.model = filters.model;
    if (filters.type) params.type = filters.type;
    if (filters.maxMileage) params.maxMileage = filters.maxMileage;
    if (filters.availability === 'today') params.availableToday = true;
    if (filters.availability === 'week') params.availableThisWeek = true;
    if (sortBy) params.sortBy = sortBy;

    // Note: Location-based filtering would require backend changes
    // For now, we'll handle radius/distance filtering in Phase 2

    return params;
  };

  // Fetch vehicles whenever filters or sort changes (debounced)
  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const params = buildAPIParams();
        const { data } = await vehiclesAPI.getAll(params);
        if (!cancelled) {
          setVehicles(data);
        }
      } catch (err) {
        console.error('Error fetching vehicles', err);
        if (!cancelled) {
          const msg =
            err.response?.data?.message ||
            err.message ||
            'Failed to load vehicles.';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300); // debounce

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy]);

  // Fetch favorites once when user logs in / changes
  useEffect(() => {
    const loadFavs = async () => {
      if (!user) {
        setFavoriteIds(new Set());
        return;
      }
      try {
        const { data } = await usersAPI.getFavorites();
        const ids = new Set((data || []).map((v) => v._id));
        setFavoriteIds(ids);
      } catch {
        setFavoriteIds(new Set());
      }
    };
    loadFavs();
  }, [user]);

  // Generate dynamic breadcrumb
  const getBreadcrumbs = () => {
    const crumbs = ['Home', 'Vehicles'];
    if (filters.make) crumbs.push(filters.make);
    if (filters.model) crumbs.push(filters.model);
    return crumbs;
  };

  // Generate dynamic page title
  const getPageTitle = () => {
    let title = 'Test drive';

    if (filters.make && filters.model) {
      title += ` ${filters.make} ${filters.model}`;
    } else if (filters.make) {
      title += ` ${filters.make}`;
    } else if (filters.type) {
      title += ` ${filters.type}`;
    } else {
      title += ' vehicles';
    }

    if (filters.location) {
      title += ` near ${filters.location}`;
    } else {
      title += ' near me';
    }

    return title;
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    updateURL(filters, sortBy);
    setShowMobileFilters(false);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };

    if (filterKey === 'verifiedOnly' || filterKey === 'includeShippable') {
      newFilters[filterKey] = false;
    } else if (filterKey === 'availability') {
      newFilters[filterKey] = 'all';
    } else if (filterKey === 'radius') {
      newFilters[filterKey] = '50';
    } else {
      newFilters[filterKey] = '';
    }

    setFilters(newFilters);
    updateURL(newFilters, sortBy);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      make: '',
      model: '',
      type: '',
      location: '',
      radius: '50',
      maxMileage: '',
      availability: 'all',
      verifiedOnly: false,
      includeShippable: false,
    };
    setFilters(resetFilters);
    updateURL(resetFilters, sortBy);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    updateURL(filters, newSort);
  };

  const handleToggleFavorite = (id) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const breadcrumbs = getBreadcrumbs();
  const pageTitle = getPageTitle();

  return (
    <div className="flex flex-col h-full bg-app-bg-light dark:bg-app-bg customer-friendly">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-customer-teal-light/30 via-white to-customer-accent-light/30 dark:from-app-bg-secondary dark:via-app-bg-secondary dark:to-app-bg-secondary border-b border-app-border-light/50 dark:border-app-border">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm mb-3">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {idx === 0 ? (
                  <Link
                    to="/"
                    className="text-customer-teal hover:text-customer-teal-hover transition-colors"
                  >
                    {crumb}
                  </Link>
                ) : (
                  <>
                    <ChevronRight className="w-4 h-4 text-app-text-secondary-light dark:text-app-text-secondary" />
                    <span className={idx === breadcrumbs.length - 1
                      ? "text-app-text-light dark:text-app-text font-medium"
                      : "text-app-text-secondary-light dark:text-app-text-secondary"
                    }>
                      {crumb}
                    </span>
                  </>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Page Title & Count */}
          <div className="flex items-baseline gap-3 mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-app-text-light dark:text-app-text">
              {pageTitle}
            </h1>
            {!loading && (
              <span className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
                {vehicles.length} {vehicles.length === 1 ? 'match' : 'matches'}
              </span>
            )}
          </div>

          {/* Mobile Filter Toggle Button */}
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="lg:hidden w-full bg-gradient-to-r from-customer-teal to-customer-teal-hover hover:from-customer-teal-hover hover:to-customer-teal-active text-white py-3 rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-customer-teal/20"
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filter Your Search
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="lg:grid lg:grid-cols-[280px,1fr] lg:gap-6">
            {/* Filters Sidebar - Desktop */}
            <aside className="hidden lg:block sticky top-6 h-fit">
              <FiltersSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
              />
            </aside>

            {/* Mobile Filter Drawer */}
            {showMobileFilters && (
              <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileFilters(false)}>
                <div
                  className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-app-bg-light dark:bg-app-bg overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-app-bg-light dark:bg-app-bg border-b border-app-border-light dark:border-app-border p-4 flex items-center justify-between z-10">
                    <h2 className="text-lg font-bold text-app-text-light dark:text-app-text">Filters</h2>
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="p-2 hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-app-text-light dark:text-app-text" />
                    </button>
                  </div>
                  <div className="p-4">
                    <FiltersSidebar
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onApplyFilters={handleApplyFilters}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Results Column */}
            <main className="space-y-4">
              {/* Active Filters Bar */}
              <ActiveFiltersBar
                filters={filters}
                onRemoveFilter={handleRemoveFilter}
                onResetAll={handleResetFilters}
              />

              {/* Sort & Search */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border p-4">
                {/* Quick Search */}
                <div className="flex-1 w-full sm:w-auto relative">
                  <Search className="w-4 h-4 text-app-text-secondary-light dark:text-app-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Quick search..."
                    value={filters.model}
                    onChange={(e) => handleFilterChange({ ...filters, model: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary text-sm"
                  />
                </div>

                {/* Sort Dropdown */}
                <SortDropdown sortBy={sortBy} onSortChange={handleSortChange} />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-900/40 text-red-300 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="text-center py-12">
                  <div className="inline-block w-10 h-10 border-4 border-customer-teal border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-app-text-secondary-light dark:text-app-text-secondary text-sm">
                    Finding awesome cars for you...
                  </p>
                </div>
              )}

              {/* No Results */}
              {!loading && vehicles.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-app-bg-secondary rounded-3xl border border-app-border-light/50 dark:border-app-border">
                  <p className="text-app-text-light dark:text-app-text text-lg font-semibold mb-2">
                    Hmm, nothing here yet!
                  </p>
                  <p className="text-app-text-secondary-light dark:text-app-text-secondary text-sm mb-4">
                    Let's try different filters - there are so many great cars to discover!
                  </p>
                  <button
                    onClick={handleResetFilters}
                    className="bg-gradient-to-r from-customer-accent to-customer-accent-hover hover:from-customer-accent-hover hover:to-customer-accent-active text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 shadow-lg shadow-customer-accent/20"
                  >
                    Start Fresh
                  </button>
                </div>
              )}

              {/* Vehicle Results Grid */}
              {!loading && vehicles.length > 0 && (
                <div className="space-y-4">
                  {vehicles.map((vehicle) => (
                    <VehicleResultCard
                      key={vehicle._id}
                      vehicle={vehicle}
                      distance={null} // TODO: Calculate distance in Phase 2
                      isFavorite={favoriteIds.has(vehicle._id)}
                      onToggleFavorite={handleToggleFavorite}
                    />
                  ))}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleCatalogScreen;
